import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trophy, CheckCircle, XCircle, Clock, Target, TrendingUp, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';
import { getTestResult } from '../../services/apihomeTestseries/apihometestseries';
import toast from 'react-hot-toast';
import apiurl from '../../services/apiendpoint/apiendpoint';

export default function TestResultpage() {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [resultData, setResultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedQuestion, setExpandedQuestion] = useState(null);

    useEffect(() => {
        const fetchTestResult = async () => {
            try {
                const { attemptId } = location.state || {};
                
                if (!attemptId) {
                    // toast.error('Invalid result data');
                    navigate('/');
                    return;
                }
                const result = await getTestResult(attemptId);
                if (result.success) {
                    setResultData(result.resdata);
                } else {
                    toast.error('Failed to load test result');
                    navigate('/');
                }
            } catch (error) {
                console.error('Error fetching test result:', error);
                toast.error('Failed to load test result');
                navigate('/');
            } finally {
                setLoading(false);
            }
        };

        fetchTestResult();
    }, [location.state, navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getPerformanceColor = (percentage) => {
    if (percentage >= 75) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceLabel = (percentage) => {
    if (percentage >= 75) return 'Excellent';
    if (percentage >= 50) return 'Good';
    return 'Needs Improvement';
  };

  const getFilteredQuestions = () => {
    if (!resultData?.answers) return [];
    
    let filtered = resultData.answers;

    if (filterSubject !== 'all') {
      filtered = filtered.filter(q => q.subject === filterSubject);
    }

    if (filterStatus === 'correct') {
      filtered = filtered.filter(q => q.is_correct === true);
    } else if (filterStatus === 'incorrect') {
      filtered = filtered.filter(q => q.is_correct === false);
    } else if (filterStatus === 'unattempted') {
      filtered = filtered.filter(q => !q.selected_option);
    }

    return filtered;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
          <p className="text-lg font-medium text-gray-700">Loading results...</p>
        </div>
      </div>
    );
  }

  if (!resultData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <p className="text-xl font-semibold text-gray-800">No result data found</p>
          <button onClick={() => navigate('/')} className="px-6 py-3 mt-4 font-semibold text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-700">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const percentage = parseFloat(resultData.percentage);
  const uniqueSubjects = [...new Set(resultData.answers.map(q => q.subject).filter(Boolean))];
  const filteredQuestions = getFilteredQuestions();

  return (
    <div className="min-h-screen py-8 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="px-4 mx-auto max-w-7xl">
        <div className="p-8 mb-6 text-center bg-white shadow-lg rounded-2xl">
          <Trophy className="w-16 h-16 mx-auto mb-4 text-yellow-500" />
          <h1 className="mb-2 text-3xl font-bold text-gray-800">Test Completed!</h1>
          <p className="text-lg text-gray-600">{resultData.test_name}</p>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6 md:grid-cols-4">
          <div className="p-6 text-center bg-white shadow-md rounded-xl">
            <Target className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <p className="mb-1 text-sm font-medium text-gray-600">Score</p>
            <p className={`text-3xl font-bold ${getPerformanceColor(percentage)}`}>
              {resultData.total_marks_obtained}/{resultData.total_marks}
            </p>
            <p className="mt-1 text-xs text-gray-500">{resultData.percentage}%</p>
          </div>

          <div className="p-6 text-center bg-white shadow-md rounded-xl">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
            <p className="mb-1 text-sm font-medium text-gray-600">Correct</p>
            <p className="text-3xl font-bold text-green-600">{resultData.correct_answers}</p>
            <p className="mt-1 text-xs text-gray-500">out of {resultData.attempted_questions}</p>
          </div>

          <div className="p-6 text-center bg-white shadow-md rounded-xl">
            <XCircle className="w-8 h-8 mx-auto mb-2 text-red-600" />
            <p className="mb-1 text-sm font-medium text-gray-600">Incorrect</p>
            <p className="text-3xl font-bold text-red-600">{resultData.incorrect_answers}</p>
            <p className="mt-1 text-xs text-gray-500">out of {resultData.attempted_questions}</p>
          </div>

          <div className="p-6 text-center bg-white shadow-md rounded-xl">
            <Clock className="w-8 h-8 mx-auto mb-2 text-purple-600" />
            <p className="mb-1 text-sm font-medium text-gray-600">Time Taken</p>
            <p className="text-3xl font-bold text-purple-600">{formatTime(resultData.time_taken)}</p>
          </div>
        </div>

        <div className="p-6 mb-6 text-center bg-white shadow-md rounded-xl">
          <TrendingUp className={`w-12 h-12 mx-auto mb-3 ${getPerformanceColor(percentage)}`} />
          <h2 className={`text-2xl font-bold ${getPerformanceColor(percentage)}`}>
            {getPerformanceLabel(percentage)}
          </h2>
          <p className="mt-2 text-gray-600">
            You attempted {resultData.attempted_questions} out of {resultData.total_questions} questions
          </p>
        </div>

        <div className="mb-6 bg-white shadow-md rounded-xl">
          <div className="flex border-b">
            <button onClick={() => setActiveTab('summary')}
              className={`flex-1 px-6 py-4 font-semibold transition-all ${
                activeTab === 'summary' ? 'text-blue-600 bg-blue-50 border-b-3 border-blue-600' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              Summary
            </button>
            <button onClick={() => setActiveTab('review')}
              className={`flex-1 px-6 py-4 font-semibold transition-all ${activeTab === 'review' ? 'text-blue-600 bg-blue-50 border-b-3 border-blue-600' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <BookOpen className="inline mr-2" size={18} />
              Question Review
            </button>
          </div>

          {activeTab === 'summary' && (
            <div className="p-6">
              <h2 className="mb-6 text-xl font-bold text-gray-800">Subject-wise Performance</h2>
              <div className="space-y-4">
                {resultData.subject_wise_results.map((subject, index) => {
                  const subjectPercentage = ((subject.marks_obtained / subject.total_marks) * 100).toFixed(2);
                  
                  return (
                    <div key={index} className="p-4 border-2 border-gray-100 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-lg font-semibold text-gray-800">{subject.subject_name}</h3>
                        <span className={`text-lg font-bold ${getPerformanceColor(parseFloat(subjectPercentage))}`}>
                          {subject.marks_obtained}/{subject.total_marks} ({subjectPercentage}%)
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm lg:grid-cols-4">
                        <div>
                          <p className="text-gray-600">Total</p>
                          <p className="font-semibold text-gray-800">{subject.total_questions}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Attempted</p>
                          <p className="font-semibold text-blue-600">{subject.attempted}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Correct</p>
                          <p className="font-semibold text-green-600">{subject.correct}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Incorrect</p>
                          <p className="font-semibold text-red-600">{subject.incorrect}</p>
                        </div>
                      </div>
                      
                      <div className="w-full h-2 mt-3 overflow-hidden bg-gray-200 rounded-full">
                        <div className="h-full transition-all duration-500 bg-gradient-to-r from-blue-500 to-green-500" style={{ width: `${subjectPercentage}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'review' && (
            <div className="p-6">
              <div className="gap-4 mb-6 space-y-2 xl:space-y-0 lg:flex">
                <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg xl:w-auto focus:border-blue-500 focus:outline-none">
                  <option value="all">All Subjects</option>
                  {uniqueSubjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>

                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg xl:w-auto focus:border-blue-500 focus:outline-none">
                  <option value="all">All Questions</option>
                  <option value="correct">Correct</option>
                  <option value="incorrect">Incorrect</option>
                  <option value="unattempted">Unattempted</option>
                </select>
              </div>

              <div className="space-y-4">
                {filteredQuestions.map((question, index) => {
                  const isExpanded = expandedQuestion === question.question_id;
                  const questionNumber = resultData.answers.findIndex(q => q.question_id === question.question_id) + 1;
                  
                  let statusColor = 'bg-gray-100 text-gray-700';
                  let statusText = 'Unattempted';
                  
                  if (question.selected_option) {
                    if (question.is_correct) {
                      statusColor = 'bg-green-100 text-green-700';
                      statusText = 'Correct';
                    } else {
                      statusColor = 'bg-red-100 text-red-700';
                      statusText = 'Incorrect';
                    }
                  }

                  return (
                    <div key={question.question_id} className="overflow-hidden border-2 border-gray-200 rounded-lg">
                      <div onClick={() =>setExpandedQuestion(isExpanded ? null : question.question_id)}
                        className="flex flex-col gap-3 p-4 cursor-pointer sm:flex-row sm:items-center sm:justify-between hover:bg-gray-50"
                      >
                        <div className="flex items-start flex-1 gap-4 sm:items-center">
                          <span className="flex items-center justify-center w-10 h-10 font-bold text-white bg-blue-600 rounded-full">
                            {questionNumber}
                          </span>

                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800 line-clamp-2 sm:line-clamp-none sm:text-base">
                              {question.question_text}
                            </p>
                            <p className="mt-1 text-xs text-gray-500 sm:text-sm">
                              {question.subject}
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-between gap-3 sm:items-center sm:justify-end">
                          <span className={`px-2.5 py-1 text-xs sm:text-sm font-semibold rounded-full ${statusColor}`}>
                            {statusText}
                          </span>

                          <span className="text-sm font-bold text-gray-700 sm:text-base">
                            {question.marks_awarded > 0 ? "+" : ""}
                            {question.marks_awarded}
                          </span>

                          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>
                      </div>


                      {isExpanded && (
                        <div className="p-6 border-t bg-gray-50">
                          <div className="mb-6">
                            <h4 className="mb-2 font-semibold text-gray-700">Question:</h4>
                            <p className="text-gray-800">{question.question_text}</p>
                            {question.diagram && (
                              <img src={`${apiurl()}/${question.diagram}`} alt="Question diagram" className="mt-3 rounded-lg max-h-64"/>
                            )}
                          </div>

                          <div className="mb-6 space-y-2">
                            <h4 className="mb-3 font-semibold text-gray-700">Options:</h4>
                          {question.options.map((option, idx) => {
                              const optionLabel = ['A', 'B', 'C', 'D'][idx];
                              
                              const hasImage = question.option_images && 
                                              question.option_images[idx] && 
                                              question.option_images[idx].trim() !== '';
                              
                              const optionValue = `option_${idx}`;
                              
                              const isCorrect = hasImage ? optionValue === question.correct_answer : option === question.correct_answer || optionValue === question.correct_answer;
                                
                              const isSelected = hasImage ? optionValue === question.selected_option : option === question.selected_option || optionValue === question.selected_option;
                              
                              let optionClass = 'border-gray-200 bg-white';
                              if (isCorrect) {
                                optionClass = 'border-green-500 bg-green-50';
                              } else if (isSelected && !isCorrect) {
                                optionClass = 'border-red-500 bg-red-50';
                              }

                              return (
                                <div key={idx} className={`p-3 border-2 rounded-lg ${optionClass}`}>
                                  <div className="flex items-start gap-3">
                                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                                      isCorrect ? 'bg-green-500 text-white' : isSelected ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-600'
                                    }`}>
                                      {optionLabel}
                                    </span>
                                    <div className="flex-1">
                                      {hasImage ? (
                                        <img src={`${apiurl()}/${question.option_images[idx]}`} alt={`Option ${optionLabel}`} className="object-contain rounded-lg max-h-32"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            console.error('Failed to load option image');
                                          }}
                                        />
                                      ) : (
                                        <span className="text-gray-800">{option}</span>
                                      )}
                                      {isCorrect && (
                                        <span className="ml-2 text-sm font-semibold text-green-600">✓ Correct Answer</span>
                                      )}
                                      {isSelected && !isCorrect && (
                                        <span className="ml-2 text-sm font-semibold text-red-600">✗ Your Answer</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {question.explanation && (
                            <div className="p-4 border-l-4 border-blue-500 rounded-r-lg bg-blue-50">
                              <h4 className="mb-2 font-semibold text-blue-800">Explanation:</h4>
                              <p className="text-gray-700">{question.explanation}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-4">
          <button onClick={() => navigate('/')} className="flex-1 px-3 py-1 font-semibold text-white transition-all duration-200 rounded-lg lg:px-6 lg:py-3 bg-regal-blue hover:bg-gradient-to-l hover:from-regal-yellow hover:to-regal-blue ">
            Back to Home
          </button>
          <button onClick={() => navigate(`/online-test-series/${location.state?.testSeriesSlug}`)}
            className="flex-1 px-3 py-1 font-semibold transition-all duration-200 bg-white border-2 rounded-lg lg:px-6 lg:py-3 text-regal-blue border-regal-blue hover:bg-blue-50"
          >
            View Test Series
          </button>
        </div>
      </div>
    </div>
  );
}